import { ITEM_TYPES, type Condition, type ItemType } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, type BomData } from '../api';
import { canWrite, useAuth } from '../auth';
import { TYPE_LABEL } from '../lib/labels';
import { resolveColor, usesColor } from '../lib/purchaseOrderForm';
import { useColors } from '../queries';
import { Badge, Button, Card, ErrorMessage, Field, Input, Notice, Select, Spinner, number, plural, useConfirm } from '../ui';
import { ColorSwatch, ItemImage } from './ItemImage';

interface Draft {
  key: number;
  itemType: ItemType;
  itemNo: string;
  colorText: string;
  condition: '' | Condition;
  qty: string;
  name: string;
}
let nextKey = 1;
const blank = (): Draft => ({ key: nextKey++, itemType: 'PART', itemNo: '', colorText: '', condition: '', qty: '1', name: '' });
const today = () => new Date().toISOString().slice(0, 10);
const COND = { N: 'New', U: 'Used' } as const;

/**
 * What this item is built from, how many could be built from the loose parts, and building and unbuilding. Selling
 * the item takes built ones first and builds the rest from parts, so parts and built units always move together.
 */
export function PartsListTab({ itemId, itemType }: { itemId: number; itemType: ItemType }) {
  const { user } = useAuth();
  const writable = canWrite(user);
  const qc = useQueryClient();
  const colors = useColors();
  const { confirm, dialog } = useConfirm();
  const bom = useQuery({ queryKey: ['bom', itemId], queryFn: () => api<BomData>(`/api/v1/items/${itemId}/bom`) });
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Draft[]>([]);
  const [fromBl, setFromBl] = useState(false);
  const [buildQty, setBuildQty] = useState<Record<string, string>>({ N: '1', U: '1' });
  const [done, setDone] = useState<string | null>(null);

  const refresh = () => {
    for (const k of ['bom', 'lots', 'lot', 'items', 'item-inventory', 'item-movements', 'dashboard', 'purchases', 'purchase']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const load = useMutation({
    mutationFn: () => api<{ lines: { itemType: ItemType; itemNo: string; name: string | null; colorId: number; colorName: string | null; qty: number }[] }>(`/api/v1/items/${itemId}/bom/from-bricklink`, { method: 'POST', body: {} }).then((r) => r.lines),
    onSuccess: (lines) => {
      setRows(lines.map((l) => ({ key: nextKey++, itemType: l.itemType, itemNo: l.itemNo, colorText: l.colorName ?? String(l.colorId), condition: '', qty: String(l.qty), name: l.name ?? '' })));
      setFromBl(true);
      setEditing(true);
    },
  });
  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/items/${itemId}/bom`, {
        method: 'PUT',
        body: {
          source: fromBl ? 'bricklink' : 'manual',
          lines: rows
            .filter((r) => r.itemNo.trim())
            .map((r) => ({ itemType: r.itemType, itemNo: r.itemNo.trim(), colorId: resolveColor(r.colorText, r.itemType, colors.data)!.id, condition: r.condition || null, qty: Number(r.qty), ...(r.name.trim() && { name: r.name.trim() }) })),
        },
      }),
    onSuccess: () => {
      setEditing(false);
      setFromBl(false);
      refresh();
    },
  });
  const makeLot = useMutation({
    mutationFn: (condition: Condition) => api(`/api/v1/items/${itemId}/assembly-lot`, { body: { condition } }),
    onSuccess: refresh,
  });
  const build = useMutation({
    mutationFn: ({ lotId, qty, kind }: { lotId: number; qty: number; kind: 'build' | 'unbuild' }) =>
      api<{ result: { built: number; cost: string; purchaseId: number } }>(`/api/v1/lots/${lotId}/${kind}`, { body: { qty, on: today() } }).then((r) => ({ ...r.result, kind })),
    onSuccess: (r) => {
      setDone(`${r.kind === 'build' ? 'Built' : 'Unbuilt'} ${r.built}.`);
      refresh();
    },
  });

  const startEdit = () => {
    setRows(
      (bom.data?.lines ?? []).map((l) => ({ key: nextKey++, itemType: l.itemType, itemNo: l.itemNo, colorText: l.colorId === 0 ? '' : l.colorName, condition: l.condition ?? '', qty: String(l.qty), name: '' })),
    );
    if (!bom.data?.lines.length) setRows([blank()]);
    setFromBl(false);
    setEditing(true);
  };

  const problems: string[] = [];
  if (editing) {
    const filled = rows.filter((r) => r.itemNo.trim());
    for (const r of filled) {
      if (!resolveColor(r.colorText, r.itemType, colors.data)) problems.push(`${r.itemNo}: choose a colour.`);
      if (!Number.isInteger(Number(r.qty)) || Number(r.qty) < 1) problems.push(`${r.itemNo}: the quantity must be 1 or more.`);
    }
  }
  const set = (key: number, patch: Partial<Draft>) => setRows((x) => x.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  if (bom.isPending) return <Spinner />;
  if (bom.error || !bom.data) return <ErrorMessage error={bom.error} />;
  const d = bom.data;
  const hasList = d.lines.length > 0;

  return (
    <div className="space-y-4">
      {dialog}
      <Card
        title="Parts list"
        actions={
          writable && !editing ? (
            <div className="flex gap-2">
              <Button variant="secondary" small disabled={load.isPending} onClick={() => load.mutate()}>
                {load.isPending ? 'Asking BrickLink…' : 'Load from BrickLink'}
              </Button>
              <Button small onClick={startEdit}>
                {hasList ? 'Edit' : 'Add parts'}
              </Button>
            </div>
          ) : undefined
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          What one {itemType === 'MINIFIG' ? 'minifigure' : 'of this item'} is made from. With a parts list you can <strong>build</strong> it from loose parts, and it can be <strong>sold</strong> either way: a sale takes built ones first and builds the rest from the parts as it is taken, so the parts and the built count always move together. Selling a part on its own lowers how many can be built.
        </p>
        <ErrorMessage error={load.error} />
        {!editing && !hasList && <p className="text-sm text-slate-500">No parts list yet. {writable ? 'Add the parts by hand, or load what BrickLink lists.' : ''}</p>}
        {!editing && hasList && (
          <div className="overflow-x-auto">
            <table className="data w-full min-w-[40rem] text-sm">
              <thead>
                <tr>
                  <th className="min-w-14" />
                  <th>Part</th>
                  <th>Colour</th>
                  <th>Cond.</th>
                  <th className="text-right">Per item</th>
                  <th className="text-right">New in stock</th>
                  <th className="text-right">Used in stock</th>
                </tr>
              </thead>
              <tbody>
                {d.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="min-w-14">
                      <ItemImage itemType={l.itemType} itemNo={l.itemNo} colorId={l.colorId} />
                    </td>
                    <td>
                      <Link to={`/items/${l.componentItemId}`} className="font-medium hover:underline">
                        {l.itemNo}
                      </Link>
                      <div className="max-w-64 truncate text-xs text-slate-500">{l.itemName}</div>
                    </td>
                    <td className="whitespace-nowrap">
                      <ColorSwatch rgb={l.colorRgb} name={l.colorName} />
                    </td>
                    <td>{l.condition ? COND[l.condition] : 'same'}</td>
                    <td className="text-right tabular-nums">{l.qty}</td>
                    <td className="text-right tabular-nums">{number(l.onHandNew)}</td>
                    <td className="text-right tabular-nums">{number(l.onHandUsed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {editing && (
          <div className="space-y-3">
            {fromBl && <Notice>This is BrickLink's list. Check it, change what you like, then save.</Notice>}
            <datalist id="bom-color-names">
              {(colors.data ?? []).filter((c) => c.id !== 0).map((c) => (
                <option key={c.id} value={c.name} />
              ))}
            </datalist>
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[46rem] text-sm">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Part number</th>
                    <th>Colour</th>
                    <th>Condition</th>
                    <th className="text-right">Per item</th>
                    <th>Name, if new</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td>
                        <Select aria-label="Type" className="w-28" value={r.itemType} onChange={(e) => set(r.key, { itemType: e.target.value as ItemType })}>
                          {ITEM_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {TYPE_LABEL[t]}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td>
                        <Input aria-label="Part number" className="w-28" value={r.itemNo} onChange={(e) => set(r.key, { itemNo: e.target.value })} />
                      </td>
                      <td>
                        <Input aria-label="Colour" className="w-40" list="bom-color-names" disabled={!usesColor(r.itemType)} placeholder={usesColor(r.itemType) ? 'Type a colour' : 'not applicable'} value={usesColor(r.itemType) ? r.colorText : ''} onChange={(e) => set(r.key, { colorText: e.target.value })} />
                      </td>
                      <td>
                        <Select aria-label="Condition" className="w-32" value={r.condition} onChange={(e) => set(r.key, { condition: e.target.value as '' | Condition })}>
                          <option value="">Same as built</option>
                          <option value="N">New</option>
                          <option value="U">Used</option>
                        </Select>
                      </td>
                      <td>
                        <Input aria-label="Per item" inputMode="numeric" className="w-16 text-right" value={r.qty} onChange={(e) => set(r.key, { qty: e.target.value })} />
                      </td>
                      <td>
                        <Input aria-label="Name if new" className="w-44" value={r.name} onChange={(e) => set(r.key, { name: e.target.value })} />
                      </td>
                      <td>
                        <Button variant="ghost" small onClick={() => setRows((x) => x.filter((y) => y.key !== r.key))}>
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="ghost" onClick={() => setRows((x) => [...x, blank()])}>
              Add a part
            </Button>
            {problems.length > 0 && (
              <Notice tone="amber">
                <ul className="list-disc pl-5">
                  {[...new Set(problems)].map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </Notice>
            )}
            <ErrorMessage error={save.error} />
            <div className="flex gap-2">
              <Button disabled={problems.length > 0 || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save parts list'}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>

      {hasList && !editing && (
        <Card title="Built and buildable">
          {done && <Notice tone="green">{done}</Notice>}
          <ErrorMessage error={build.error} />
          <div className="grid gap-4 sm:grid-cols-2">
            {d.conditions.map((c) => (
              <div key={c.condition} className="rounded border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-medium">{COND[c.condition]}</span>
                  {c.lotId ? <Link to={`/lots/${c.lotId}`} className="text-xs text-blue-700 hover:underline">Open the lot</Link> : null}
                </div>
                <dl className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div>
                    <dt className="text-xs text-slate-500">Built</dt>
                    <dd className="text-xl font-semibold">{number(c.built)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Can build</dt>
                    <dd className="text-xl font-semibold">{number(c.buildable)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Can sell</dt>
                    <dd className="text-xl font-semibold">{number(c.built + c.buildable)}</dd>
                  </div>
                </dl>
                {c.limitedBy && <p className="mt-2 text-xs text-slate-500">{c.buildable === 0 ? `Can't build any: short of ${c.limitedBy}.` : `Limited by ${c.limitedBy}.`}</p>}
                {writable &&
                  (c.lotId ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Input aria-label={`How many, ${COND[c.condition]}`} inputMode="numeric" className="w-20 text-right" value={buildQty[c.condition] ?? '1'} onChange={(e) => setBuildQty({ ...buildQty, [c.condition]: e.target.value })} />
                      <Button
                        small
                        disabled={build.isPending || !(Number(buildQty[c.condition]) >= 1) || Number(buildQty[c.condition]) > c.buildable}
                        onClick={async () => {
                          const n = Number(buildQty[c.condition]);
                          if (await confirm(`Build ${plural(n, 'item')} from loose parts? The parts are used up and the built items come in at what the parts cost.`, { label: 'Build' })) build.mutate({ lotId: c.lotId!, qty: n, kind: 'build' });
                        }}
                      >
                        Build
                      </Button>
                      <Button
                        small
                        variant="secondary"
                        disabled={build.isPending || !(Number(buildQty[c.condition]) >= 1) || Number(buildQty[c.condition]) > c.built}
                        onClick={async () => {
                          const n = Number(buildQty[c.condition]);
                          if (await confirm(`Take ${plural(n, 'built item')} apart? The parts go back into stock at what the built items cost.`, { label: 'Take apart' })) build.mutate({ lotId: c.lotId!, qty: n, kind: 'unbuild' });
                        }}
                      >
                        Take apart
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <Button small variant="secondary" disabled={makeLot.isPending} onClick={() => makeLot.mutate(c.condition)}>
                        Make it sellable ({COND[c.condition]})
                      </Button>
                      <p className="mt-1 text-xs text-slate-500">Creates the lot so it can be built, sold and listed before any are built.</p>
                    </div>
                  ))}
                {c.lotId && c.built + c.buildable > 0 && <Badge tone="green">sellable</Badge>}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            "Can build" counts the loose parts each figure needs. The same parts count towards every item that uses them and towards selling them on their own, so these numbers overlap: one part can only be used once.
          </p>
        </Card>
      )}
    </div>
  );
}
